import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, Plus, Tag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface ServerTagManagerProps {
  serverId: string;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  allTags: string[];
}

const TAG_COLORS: Record<string, string> = {
  production: "bg-destructive/15 text-destructive border-destructive/30",
  staging: "bg-warning/15 text-warning border-warning/30",
  development: "bg-primary/15 text-primary border-primary/30",
  dev: "bg-primary/15 text-primary border-primary/30",
  prod: "bg-destructive/15 text-destructive border-destructive/30",
  test: "bg-accent/50 text-accent-foreground border-accent",
  database: "bg-success/15 text-success border-success/30",
  web: "bg-chart-1/15 text-chart-1 border-chart-1/30",
  api: "bg-chart-2/15 text-chart-2 border-chart-2/30",
};

function getTagColor(tag: string): string {
  const lower = tag.toLowerCase();
  return TAG_COLORS[lower] || "bg-muted text-muted-foreground border-border";
}

export function ServerTagManager({ serverId, tags, onTagsChange, allTags }: ServerTagManagerProps) {
  const [newTag, setNewTag] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const addTag = async (tag: string) => {
    const trimmed = tag.trim().toLowerCase();
    if (!trimmed || tags.includes(trimmed)) return;

    const updatedTags = [...tags, trimmed];
    try {
      const { error } = await (supabase.from("servers") as any)
        .update({ tags: updatedTags })
        .eq("id", serverId);

      if (error) throw error;
      onTagsChange(updatedTags);
      setNewTag("");
    } catch (error) {
      console.error("Error adding tag:", error);
      toast({ title: "Error adding tag", variant: "destructive" });
    }
  };

  const removeTag = async (tagToRemove: string) => {
    const updatedTags = tags.filter((t) => t !== tagToRemove);
    try {
      const { error } = await (supabase.from("servers") as any)
        .update({ tags: updatedTags })
        .eq("id", serverId);

      if (error) throw error;
      onTagsChange(updatedTags);
    } catch (error) {
      console.error("Error removing tag:", error);
      toast({ title: "Error removing tag", variant: "destructive" });
    }
  };

  const suggestedTags = allTags.filter((t) => !tags.includes(t) && t.includes(newTag.toLowerCase()));

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {tags.map((tag) => (
        <Badge key={tag} variant="outline" className={`text-xs ${getTagColor(tag)} cursor-default`}>
          {tag}
          <button onClick={() => removeTag(tag)} className="ml-1 hover:opacity-70">
            <X className="h-2.5 w-2.5" />
          </button>
        </Badge>
      ))}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="h-5 w-5">
            <Plus className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-52 p-2" align="start">
          <Input
            placeholder="Add tag..."
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newTag.trim()) {
                addTag(newTag);
              }
            }}
            className="h-7 text-xs mb-1"
            autoFocus
          />
          {suggestedTags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {suggestedTags.slice(0, 8).map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className={`text-xs cursor-pointer hover:opacity-80 ${getTagColor(tag)}`}
                  onClick={() => {
                    addTag(tag);
                    setIsOpen(false);
                  }}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function TagFilterBar({
  allTags,
  selectedTags,
  onSelectedTagsChange,
}: {
  allTags: string[];
  selectedTags: string[];
  onSelectedTagsChange: (tags: string[]) => void;
}) {
  if (allTags.length === 0) return null;

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onSelectedTagsChange(selectedTags.filter((t) => t !== tag));
    } else {
      onSelectedTagsChange([...selectedTags, tag]);
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Tag className="h-3.5 w-3.5 text-muted-foreground" />
      {allTags.map((tag) => (
        <Badge
          key={tag}
          variant={selectedTags.includes(tag) ? "default" : "outline"}
          className={`text-xs cursor-pointer transition-all ${
            selectedTags.includes(tag) ? "" : getTagColor(tag) + " hover:opacity-80"
          }`}
          onClick={() => toggleTag(tag)}
        >
          {tag}
        </Badge>
      ))}
      {selectedTags.length > 0 && (
        <Button
          variant="ghost"
          size="sm"
          className="h-5 text-xs px-2"
          onClick={() => onSelectedTagsChange([])}
        >
          Clear
        </Button>
      )}
    </div>
  );
}
